import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Snackbar,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Divider,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  ListItemButton
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
  DragIndicator as DragIndicatorIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Radio as RadioIcon,
  Search as SearchIcon,
  MusicNote as MusicNoteIcon,
  Clear as ClearIcon,
  UploadFile as UploadFileIcon,
  InfoOutlined as InfoOutlinedIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  SkipNext as SkipNextIcon
} from '@mui/icons-material';
import { radioAPI } from '../services/api';

// Компонент заголовка с поиском (без dropdown)
const SearchTableHeader = ({ label, value, onChange, placeholder }) => {
  return (
    <TableCell sx={{ p: 1, minWidth: 120 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <TextField
          size="small"
          placeholder={placeholder || 'Поиск...'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          autoComplete="off"
          InputProps={{
            endAdornment: value ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => onChange('')}
                  sx={{ p: 0.25 }}
                >
                  <ClearIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        />
      </Box>
    </TableCell>
  );
};

// Компонент заголовка с селектором статуса
const StatusFilterHeader = ({ label, value, onChange }) => {
  return (
    <TableCell sx={{ p: 1, minWidth: 120 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem' }}>
          {label}
        </Typography>
        <TextField
          select
          size="small"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          SelectProps={{
            native: true,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '0.75rem',
            },
            '& .MuiInputBase-input': {
              py: 0.5,
              px: 0.5
            }
          }}
        >
          <option value="all">Все</option>
          <option value="active">Активен</option>
          <option value="inactive">Не активен</option>
        </TextField>
      </Box>
    </TableCell>
  );
};

// Компонент для сортируемой строки таблицы
const SortableTableRow = ({ channel, index, children, isDragDropEnabled }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: channel.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <TableRow
      ref={setNodeRef}
      style={style}
      sx={{
        cursor: isDragDropEnabled ? (isDragging ? 'grabbing' : 'grab') : 'default',
        '&:hover': {
          backgroundColor: 'action.hover',
        },
      }}
      {...(isDragDropEnabled ? attributes : {})}
      {...(isDragDropEnabled ? listeners : {})}
    >
      {children}
    </TableRow>
  );
};

// Компонент карточки канала для мобильных устройств
const SortableChannelCard = ({ 
  channel, 
  index, 
  onEdit, 
  onDelete, 
  onMoveUp, 
  onMoveDown, 
  isDragDropEnabled, 
  canMoveUp, 
  canMoveDown,
  onShowDragDropWarning 
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: channel.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getStreamHostname = (streamUrl) => {
    if (!streamUrl) return null;
    try {
      const hostname = new URL(streamUrl).hostname;
      if (hostname.includes('hostingradio.ru')) return 'Hosting Radio';
      if (hostname.includes('101.ru')) return '101.ru';
      if (hostname.includes('streamtheworld.com')) return 'StreamTheWorld';
      if (hostname.includes('zeno.fm')) return 'Zeno.FM';
      if (hostname.includes('brasilstream.com.br')) return 'Brasil Stream';
      return hostname;
    } catch {
      return null;
    }
  };

  return (
    <Card 
      ref={setNodeRef} 
      style={style} 
      sx={{ 
        mb: 1, 
        border: isDragging ? '2px dashed #1976d2' : '1px solid #e0e0e0',
        backgroundColor: isDragging ? '#f5f5f5' : 'white',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        {/* Первая строка */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem', fontWeight: 600, minWidth: '24px', textAlign: 'center' }}>
            #{index + 1}
          </Typography>
          <Avatar src={channel.icon} alt={channel.name} sx={{ width: 36, height: 36 }}>
            <RadioIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body1" sx={{ fontSize: '0.9rem', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mb: 0.25 }}>
              {channel.name || 'Без названия'}
            </Typography>
            {getStreamHostname(channel.stream) && (
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                <MusicNoteIcon sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                {getStreamHostname(channel.stream)}
              </Typography>
            )}
          </Box>
          <Chip label={channel.is_active ? 'Активен' : 'Отключен'} color={channel.is_active ? 'success' : 'error'} size="small" sx={{ fontSize: '0.7rem' }} />
        </Box>

        {/* Кнопки управления */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          {isDragDropEnabled ? (
            <Box {...attributes} {...listeners} sx={{ cursor: 'grab', p: 1, borderRadius: 1, backgroundColor: 'action.hover', '&:active': { cursor: 'grabbing' }, display: 'flex', alignItems: 'center', minWidth: '44px', minHeight: '44px', justifyContent: 'center' }}>
              <DragIndicatorIcon sx={{ fontSize: '18px' }} />
            </Box>
          ) : (
            <>
              <IconButton size="medium" onClick={() => onMoveUp(channel.id)} disabled={!canMoveUp} sx={{ minWidth: '44px', minHeight: '44px', backgroundColor: 'action.hover' }}>
                <ArrowUpwardIcon sx={{ fontSize: '18px' }} />
              </IconButton>
              <IconButton size="medium" onClick={() => onMoveDown(channel.id)} disabled={!canMoveDown} sx={{ minWidth: '44px', minHeight: '44px', backgroundColor: 'action.hover' }}>
                <ArrowDownwardIcon sx={{ fontSize: '18px' }} />
              </IconButton>
            </>
          )}
          <IconButton size="medium" onClick={() => { if (isDragDropEnabled) { onShowDragDropWarning && onShowDragDropWarning(); return; } onEdit(channel); }} sx={{ minWidth: '44px', minHeight: '44px', backgroundColor: 'primary.light', color: 'primary.contrastText' }}>
            <EditIcon sx={{ fontSize: '18px' }} />
          </IconButton>
          <IconButton size="medium" onClick={() => { if (isDragDropEnabled) { onShowDragDropWarning && onShowDragDropWarning(); return; } onDelete(channel); }} sx={{ minWidth: '44px', minHeight: '44px', backgroundColor: 'error.light', color: 'error.contrastText' }}>
            <DeleteIcon sx={{ fontSize: '18px' }} />
          </IconButton>
        </Box>
      </CardContent>
    </Card>
  );
};

const RadioPlaylistChannelsPage = () => {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [playlist, setPlaylist] = useState(null);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [channelToDelete, setChannelToDelete] = useState(null);
  
  // Состояния для фильтрации
  const [nameSearch, setNameSearch] = useState('');
  const [streamSearch, setStreamSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'active', 'inactive'
  
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [allChannels, setAllChannels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  const [isDragDropEnabled, setIsDragDropEnabled] = useState(() => {
    const saved = localStorage.getItem('radioDragDropEnabled');
    return saved === 'true';
  });
  const [showDragDropWarning, setShowDragDropWarning] = useState(false);

  // Import states
  const fileInputRef = useRef(null);
  const [importInfoOpen, setImportInfoOpen] = useState(false);
  const [importResultsOpen, setImportResultsOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);

  const handleDragDropToggle = (checked) => {
    setIsDragDropEnabled(checked);
    localStorage.setItem('radioDragDropEnabled', checked.toString());
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    loadPlaylistAndChannels();
    loadAllChannels();
  }, [playlistId]);

  const loadPlaylistAndChannels = async () => {
    try {
      setLoading(true);
      setError('');
      
      const playlistResponse = await radioAPI.getPlaylistById(playlistId);
      setPlaylist(playlistResponse.data);
      
      const channelsResponse = await radioAPI.getPlaylistChannels(playlistId);
      setChannels(channelsResponse.data);
    } catch (err) {
      console.error('Error loading playlist and channels:', err);
      setError('Ошибка загрузки плейлиста и каналов');
    } finally {
      setLoading(false);
    }
  };

  const loadAllChannels = async () => {
    try {
      const response = await radioAPI.getChannels();
      setAllChannels(response.data);
    } catch (error) {
      console.error('Ошибка загрузки списка каналов:', error);
    }
  };

  const handleAddExistingChannel = async (channel) => {
    try {
      const maxSortOrder = channels.length > 0 ? Math.max(...channels.map(c => c.sort_order)) : 0;
      await radioAPI.addChannelToPlaylist(playlistId, channel.id, maxSortOrder + 1);
      setSnackbar({ open: true, message: 'Канал добавлен в плейлист', severity: 'success' });
      setOpenAddDialog(false);
      loadPlaylistAndChannels();
    } catch (error) {
      console.error('Ошибка добавления канала:', error);
      setSnackbar({ open: true, message: 'Ошибка добавления канала', severity: 'error' });
    }
  };

  const handleRemoveChannel = async (channelId) => {
    try {
      await radioAPI.removeChannelFromPlaylist(playlistId, channelId);
      await loadPlaylistAndChannels();
      setDeleteDialogOpen(false);
      setChannelToDelete(null);
    } catch (err) {
      console.error('Error removing channel:', err);
      setError('Ошибка удаления канала из плейлиста');
    }
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = channels.findIndex((channel) => channel.id === active.id);
      const newIndex = channels.findIndex((channel) => channel.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newChannels = arrayMove(channels, oldIndex, newIndex);
        setChannels(newChannels);

        try {
          await radioAPI.reorderChannels(playlistId, {
            channel_id: active.id,
            new_position: newIndex
          });
          await loadPlaylistAndChannels();
        } catch (err) {
          console.error('Error reordering channels:', err);
          setError('Ошибка изменения порядка каналов');
          await loadPlaylistAndChannels();
        }
      }
    }
  };

  const checkDragDropModeForEditDelete = () => {
    if (isDragDropEnabled) {
      setShowDragDropWarning(true);
      return true;
    }
    return false;
  };

  const checkDragDropModeForArrows = () => {
    if (isDragDropEnabled) {
      setShowDragDropWarning(true);
      return true;
    }
    return false;
  };

  const handleMoveChannel = async (channelId, direction) => {
    if (checkDragDropModeForArrows()) {
      return;
    }

    try {
      const currentIndex = channels.findIndex(ch => ch.id === channelId);
      const targetIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
      
      if (targetIndex >= 0 && targetIndex < channels.length) {
        const currentChannel = channels[currentIndex];
        const targetChannel = channels[targetIndex];
        
        await radioAPI.reorderChannels(playlistId, {
          channel1_id: currentChannel.id,
          channel2_id: targetChannel.id
        });
        
        await loadPlaylistAndChannels();
      }
    } catch (err) {
      console.error('Error moving channel:', err);
      setError('Ошибка изменения порядка канала');
    }
  };

  const handleCreateNewChannel = () => {
    navigate(`/radio/channels/new?playlistId=${playlistId}`);
  };

  const handleImportFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    // Reset so the same file can be re-imported if needed
    e.target.value = '';

    const reader = new FileReader();
    reader.onload = async (event) => {
      let jsonData;
      try {
        jsonData = JSON.parse(event.target.result);
      } catch {
        setSnackbar({ open: true, message: 'Ошибка: файл не является валидным JSON', severity: 'error' });
        return;
      }
      if (!Array.isArray(jsonData)) {
        setSnackbar({ open: true, message: 'Ошибка: JSON должен содержать массив каналов', severity: 'error' });
        return;
      }

      setImporting(true);

      // Reload current channels & all channels to have fresh state
      let currentChannels = channels;
      let systemChannels = allChannels;
      try {
        const [playlistRes, allRes] = await Promise.all([
          radioAPI.getPlaylistChannels(playlistId),
          radioAPI.getChannels()
        ]);
        currentChannels = playlistRes.data;
        systemChannels = allRes.data;
      } catch (err) {
        console.error('Error refreshing channels before import:', err);
      }

      const existingUrls = new Set(currentChannels.map(ch => ch.url));
      const systemByUrl = {};
      systemChannels.forEach(ch => { systemByUrl[ch.url] = ch; });

      let added = 0;
      let skipped = 0;
      let errors = 0;
      const errorDetails = [];

      const maxSortOrder = currentChannels.length > 0
        ? Math.max(...currentChannels.map(c => c.sort_order))
        : 0;
      let sortOrder = maxSortOrder;

      for (const item of jsonData) {
        if (!item.url) { errors++; errorDetails.push(`Канал без url: ${item.name || '?'}`); continue; }

        if (existingUrls.has(item.url)) {
          skipped++;
          continue;
        }

        try {
          let channelId;
          if (systemByUrl[item.url]) {
            channelId = systemByUrl[item.url].id;
          } else {
            const res = await radioAPI.createChannel({
              url: item.url,
              name: item.name || item.url,
              icon: item.icon || '',
              stream: item.stream || '',
              is_active: true,
              sort_order: 0
            });
            channelId = res.data.id;
            systemByUrl[item.url] = res.data;
          }

          sortOrder++;
          await radioAPI.addChannelToPlaylist(playlistId, channelId, sortOrder);
          existingUrls.add(item.url);
          added++;
        } catch (err) {
          console.error('Import error for channel:', item, err);
          errors++;
          errorDetails.push(`${item.name || item.url}: ${err.response?.data?.detail || err.message}`);
        }
      }

      setImporting(false);
      setImportResults({ total: jsonData.length, added, skipped, errors, errorDetails });
      setImportResultsOpen(true);
      loadPlaylistAndChannels();
      loadAllChannels();
    };
    reader.readAsText(file);
  };

  const getStreamHostname = (streamUrl) => {
    if (!streamUrl) return null;
    try {
      const hostname = new URL(streamUrl).hostname;
      if (hostname.includes('hostingradio.ru')) return 'Hosting Radio';
      if (hostname.includes('101.ru')) return '101.ru';
      if (hostname.includes('streamtheworld.com')) return 'StreamTheWorld';
      if (hostname.includes('zeno.fm')) return 'Zeno.FM';
      if (hostname.includes('brasilstream.com.br')) return 'Brasil Stream';
      return hostname;
    } catch {
      return null;
    }
  };

  // Фильтрация каналов
  const filteredChannels = channels.filter(channel => {
    // Фильтр по названию
    if (nameSearch && !channel.name?.toLowerCase().includes(nameSearch.toLowerCase())) {
      return false;
    }
    // Фильтр по stream
    if (streamSearch && !channel.stream?.toLowerCase().includes(streamSearch.toLowerCase())) {
      return false;
    }
    // Фильтр по статусу
    if (statusFilter === 'active' && !channel.is_active) {
      return false;
    }
    if (statusFilter === 'inactive' && channel.is_active) {
      return false;
    }
    return true;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Заголовок */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center">
          <IconButton onClick={() => navigate('/radio/playlists')} sx={{ mr: 1 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            {playlist?.name || 'Радио плейлист'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" sx={{ ml: 2 }}>
            Управление каналами
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          <FormControlLabel
            control={
              <Switch
                checked={isDragDropEnabled}
                onChange={(e) => handleDragDropToggle(e.target.checked)}
                color="primary"
              />
            }
            label="Режим перетаскивания"
          />

          {/* Hidden file input for JSON import */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            style={{ display: 'none' }}
            onChange={handleImportFileSelect}
          />

          <Tooltip title="Пример формата JSON для импорта">
            <IconButton onClick={() => setImportInfoOpen(true)} size="small">
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            startIcon={importing ? <CircularProgress size={16} /> : <UploadFileIcon />}
            onClick={() => !importing && fileInputRef.current?.click()}
            disabled={importing}
          >
            {importing ? 'Импорт...' : 'Импорт JSON'}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setOpenAddDialog(true)}
          >
            Добавить канал
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Адаптивное отображение каналов */}
      {isMobile ? (
        /* МОБИЛЬНАЯ ВЕРСИЯ - КАРТОЧКИ */
        <DndContext 
          sensors={sensors} 
          collisionDetection={closestCenter} 
          onDragEnd={handleDragEnd}
        >
          <SortableContext 
            items={filteredChannels.map(channel => channel.id)} 
            strategy={verticalListSortingStrategy}
          >
            <Box sx={{ width: '100%', maxWidth: '100vw', overflow: 'hidden', px: 0 }}>
              {filteredChannels.map((channel, index) => (
                <SortableChannelCard
                  key={channel.id}
                  channel={channel}
                  index={index}
                  onEdit={(channel) => navigate(`/radio/playlists/${playlistId}/channels/${channel.id}/edit`)}
                  onDelete={(channel) => {
                    setChannelToDelete(channel);
                    setDeleteDialogOpen(true);
                  }}
                  onMoveUp={(channelId) => handleMoveChannel(channelId, 'up')}
                  onMoveDown={(channelId) => handleMoveChannel(channelId, 'down')}
                  isDragDropEnabled={isDragDropEnabled}
                  canMoveUp={index > 0}
                  canMoveDown={index < channels.length - 1}
                  onShowDragDropWarning={() => setShowDragDropWarning(true)}
                />
              ))}
            </Box>
          </SortableContext>
        </DndContext>
      ) : (
        /* ДЕСКТОПНАЯ ВЕРСИЯ - ТАБЛИЦА */
        isDragDropEnabled ? (
          <DndContext 
            sensors={sensors} 
            collisionDetection={closestCenter} 
            onDragEnd={handleDragEnd}
          >
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ p: 1, width: 100 }}>Порядок</TableCell>
                    <SearchTableHeader 
                      label="Канал" 
                      value={nameSearch} 
                      onChange={setNameSearch}
                      placeholder="Поиск по названию..."
                    />
                    <SearchTableHeader 
                      label="Stream" 
                      value={streamSearch} 
                      onChange={setStreamSearch}
                      placeholder="Поиск по stream..."
                    />
                    <StatusFilterHeader 
                      label="Статус" 
                      value={statusFilter} 
                      onChange={setStatusFilter}
                    />
                    <TableCell align="right" sx={{ p: 1 }}>Действия</TableCell>
                  </TableRow>
                </TableHead>
                <SortableContext 
                  items={filteredChannels.map(channel => channel.id)} 
                  strategy={verticalListSortingStrategy}
                >
                  <TableBody>
                    {filteredChannels.map((channel, index) => (
                      <SortableTableRow key={channel.id} channel={channel} index={index} isDragDropEnabled={isDragDropEnabled}>
                        <TableCell sx={{ p: 1 }}>
                          <Box display="flex" alignItems="center">
                            {isDragDropEnabled && <DragIndicatorIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '1.2rem' }} />}
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{index + 1}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ p: 1 }}>
                          <Box display="flex" alignItems="center">
                            <Avatar
                              src={channel.icon}
                              alt={channel.name}
                              sx={{ width: 28, height: 28, mr: 1.5 }}
                            >
                              <RadioIcon />
                            </Avatar>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{channel.name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ p: 1 }}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '0.875rem' }}>
                            {getStreamHostname(channel.stream) || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ p: 1 }}>
                          <Chip
                            label={channel.is_active ? 'Активен' : 'Отключен'}
                            color={channel.is_active ? 'success' : 'error'}
                            size="small"
                            sx={{ fontSize: '0.7rem', height: 22 }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ p: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            Перетащите для изменения порядка
                          </Typography>
                        </TableCell>
                      </SortableTableRow>
                    ))}
                  </TableBody>
                </SortableContext>
              </Table>
            </TableContainer>
          </DndContext>
        ) : (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ p: 1, width: 100 }}>Порядок</TableCell>
                  <SearchTableHeader 
                    label="Канал" 
                    value={nameSearch} 
                    onChange={setNameSearch}
                    placeholder="Поиск по названию..."
                  />
                  <SearchTableHeader 
                    label="Stream" 
                    value={streamSearch} 
                    onChange={setStreamSearch}
                    placeholder="Поиск по stream..."
                  />
                  <StatusFilterHeader 
                    label="Статус" 
                    value={statusFilter} 
                    onChange={setStatusFilter}
                  />
                  <TableCell align="right" sx={{ p: 1 }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredChannels.map((channel, index) => (
                  <SortableTableRow key={channel.id} channel={channel} index={index} isDragDropEnabled={isDragDropEnabled}>
                    <TableCell sx={{ p: 1 }}>
                      <Box display="flex" alignItems="center">
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{index + 1}</Typography>
                        <Box ml={1}>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveChannel(channel.id, 'up');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={index === 0}
                            sx={{ p: 0.5 }}
                          >
                            <ArrowUpwardIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMoveChannel(channel.id, 'down');
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            disabled={index === filteredChannels.length - 1}
                            sx={{ p: 0.5 }}
                          >
                            <ArrowDownwardIcon sx={{ fontSize: '1rem' }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Box display="flex" alignItems="center">
                        <Avatar
                          src={channel.icon}
                          alt={channel.name}
                          sx={{ width: 28, height: 28, mr: 1.5 }}
                        >
                          <RadioIcon />
                        </Avatar>
                        <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{channel.name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 200, fontSize: '0.875rem' }}>
                        {getStreamHostname(channel.stream) || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ p: 1 }}>
                      <Chip
                        label={channel.is_active ? 'Активен' : 'Отключен'}
                        color={channel.is_active ? 'success' : 'error'}
                        size="small"
                        sx={{ fontSize: '0.7rem', height: 22 }}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ p: 1 }}>
                      <Tooltip title="Редактировать канал">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (checkDragDropModeForEditDelete()) return;
                            navigate(`/radio/playlists/${playlistId}/channels/${channel.id}/edit`);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить из плейлиста">
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation();
                            if (checkDragDropModeForEditDelete()) return;
                            setChannelToDelete(channel);
                            setDeleteDialogOpen(true);
                          }}
                          onMouseDown={(e) => e.stopPropagation()}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </SortableTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {channels.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            В плейлисте пока нет каналов
          </Typography>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => navigate(`/radio/channels/new?playlistId=${playlistId}`)}
            sx={{ mt: 2 }}
          >
            Добавить первый канал
          </Button>
        </Box>
      )}

      {/* Диалог добавления канала */}
      <Dialog 
        open={openAddDialog} 
        onClose={() => setOpenAddDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Добавить канал в плейлист</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2, mt: 1 }}>
            <TextField
              fullWidth
              placeholder="Поиск по названию..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <List sx={{ maxHeight: 400, overflow: 'auto' }}>
            {allChannels
              .filter(channel => 
                !channels.some(pc => pc.id === channel.id) &&
                (channel.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                 channel.description?.toLowerCase().includes(searchTerm.toLowerCase()))
              )
              .map((channel) => (
                <ListItem key={channel.id} disablePadding>
                  <ListItemButton onClick={() => handleAddExistingChannel(channel)}>
                    <Avatar
                      src={channel.icon}
                      alt={channel.name}
                      sx={{ width: 40, height: 40, mr: 2 }}
                    >
                      <RadioIcon />
                    </Avatar>
                    <ListItemText
                      primary={channel.name || 'Без названия'}
                      secondary={getStreamHostname(channel.stream) || 'Stream не указан'}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            {allChannels.filter(channel => 
              !channels.some(pc => pc.id === channel.id) &&
              (channel.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
               channel.description?.toLowerCase().includes(searchTerm.toLowerCase()))
            ).length === 0 && (
              <Typography color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {searchTerm ? 'Каналы не найдены' : 'Все каналы уже добавлены в плейлист'}
              </Typography>
            )}
          </List>
          <Divider sx={{ my: 2 }} />
          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setOpenAddDialog(false);
              handleCreateNewChannel();
            }}
          >
            Создать новый канал
          </Button>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог подтверждения удаления */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Подтвердите удаление</DialogTitle>
        <DialogContent>
          <Typography>
            Вы уверены, что хотите удалить канал "{channelToDelete?.name}" из плейлиста?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Канал будет удален только из этого плейлиста, но останется доступным для добавления в другие плейлисты.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Отмена
          </Button>
          <Button 
            onClick={() => handleRemoveChannel(channelToDelete?.id)} 
            color="error" 
            variant="contained"
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar для уведомлений */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Snackbar для предупреждения о drag-and-drop режиме */}
      <Snackbar
        open={showDragDropWarning}
        autoHideDuration={3000}
        onClose={() => setShowDragDropWarning(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowDragDropWarning(false)}
          severity="warning"
          sx={{ width: '100%' }}
        >
          Выключите режим перетаскивания для использования кнопок действий
        </Alert>
      </Snackbar>

      {/* Диалог: пример формата импорта */}
      <Dialog open={importInfoOpen} onClose={() => setImportInfoOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Формат файла для импорта</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Выберите JSON-файл со списком радиостанций. Каналы, у которых <strong>url</strong> совпадает с уже добавленными в плейлист, будут пропущены.
          </Typography>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Пример файла:</Typography>
          <Box
            component="pre"
            sx={{
              backgroundColor: 'grey.100',
              borderRadius: 1,
              p: 2,
              fontSize: '0.78rem',
              overflowX: 'auto',
              fontFamily: 'monospace',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
{`[
  {
    "url": "europa-plus",
    "name": "Europa Plus",
    "icon": "https://example.com/img/europa.jpg",
    "stream": "http://ep256.hostingradio.ru:8052/europaplus256.mp3"
  },
  {
    "url": "rock-fm",
    "name": "ROCK FM",
    "icon": "https://example.com/img/rockfm.jpg",
    "stream": "http://nashe1.hostingradio.ru/rock-128.mp3"
  }
]`}
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            <strong>url</strong> — уникальный идентификатор (обязательно)<br/>
            <strong>name</strong> — название станции<br/>
            <strong>icon</strong> — ссылка на обложку<br/>
            <strong>stream</strong> — ссылка на аудиопоток
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportInfoOpen(false)}>Закрыть</Button>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            onClick={() => {
              setImportInfoOpen(false);
              fileInputRef.current?.click();
            }}
          >
            Выбрать файл
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог: результаты импорта */}
      <Dialog open={importResultsOpen} onClose={() => setImportResultsOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Результаты импорта</DialogTitle>
        <DialogContent>
          {importResults && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon color="success" />
                <Typography>Добавлено: <strong>{importResults.added}</strong></Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SkipNextIcon color="action" />
                <Typography>Пропущено (уже есть): <strong>{importResults.skipped}</strong></Typography>
              </Box>
              {importResults.errors > 0 && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <ErrorOutlineIcon color="error" sx={{ mt: 0.25 }} />
                  <Box>
                    <Typography>Ошибок: <strong>{importResults.errors}</strong></Typography>
                    {importResults.errorDetails.map((d, i) => (
                      <Typography key={i} variant="caption" color="error" display="block">{d}</Typography>
                    ))}
                  </Box>
                </Box>
              )}
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Всего в файле: {importResults.total}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportResultsOpen(false)} variant="contained">Готово</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RadioPlaylistChannelsPage;
